<p-toast position="top-center" />

<div class="flex flex-col h-full gap-(--gap-normal)">
  <div class="flex-1 overflow-auto">
    @if (isLoading()) {
      <ces-grid-skeleton [columns]="skeletonColumns()" [lines]="10" />
    } @else {
      <p-table
        #dt
        [value]="rows()"
        styleClass="p-datatable-sm p-datatable-gridlines w-full"
        [scrollable]="isDialog"
        scrollHeight="flex"
        [paginator]="!isDialog"
        [alwaysShowPaginator]="false"
        [paginatorDropdownAppendTo]="'body'"
        [rows]="PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE"
        [rowsPerPageOptions]="PAGINATION_CONSTANTS.PAGE_SIZE_OPTIONS"
        dataKey="id"
        [rowTrackBy]="trackById"
        [filters]="tableFilters()"
        [globalFilterFields]="['name', 'typeName', 'localisation']">
        <ng-template pTemplate="header">
          <!-- Main Header Row -->
          <tr>
            @if (!readOnly()) {
              <th style="width: 1%; white-space: nowrap"></th>
            }
            <th [style.width]="planningMode() === 'measures' ? '25%' : '35%'" [pSortableColumn]="'name'">
              <div class="flex items-center">
                {{ 'common.labels.name' | transloco }}
                @if (dt.sortField === 'name') {
                  <ces-icon aria-hidden="true">{{ dt.sortOrder === -1 ? 'south' : 'north' }}</ces-icon>
                } @else {
                  <ces-icon aria-hidden="true" rotate="90">sync_alt</ces-icon>
                }
              </div>
            </th>
            @if (planningMode() === 'measures') {
              <th style="width: 20%" [pSortableColumn]="'typeName'">
                <div class="flex items-center">
                  {{ 'common.labels.type' | transloco }}
                  @if (dt.sortField === 'typeName') {
                    <ces-icon aria-hidden="true">{{ dt.sortOrder === -1 ? 'south' : 'north' }}</ces-icon>
                  } @else {
                    <ces-icon aria-hidden="true" rotate="90">sync_alt</ces-icon>
                  }
                </div>
              </th>
            }
            <th style="width: 1%; white-space: nowrap" [pSortableColumn]="'obligatoire'" class="text-center">
              <div class="flex items-center justify-center">
                {{ 'common.labels.mandatory' | transloco }}
                @if (dt.sortField === 'obligatoire') {
                  <ces-icon aria-hidden="true">{{ dt.sortOrder === -1 ? 'south' : 'north' }}</ces-icon>
                } @else {
                  <ces-icon aria-hidden="true" rotate="90">sync_alt</ces-icon>
                }
              </div>
            </th>
            <th style="width: 25%" [pSortableColumn]="'localisation'">
              <div class="flex items-center">
                {{ 'entities.location.singular' | transloco }}
                @if (dt.sortField === 'localisation') {
                  <ces-icon aria-hidden="true">{{ dt.sortOrder === -1 ? 'south' : 'north' }}</ces-icon>
                } @else {
                  <ces-icon aria-hidden="true" rotate="90">sync_alt</ces-icon>
                }
              </div>
            </th>
            @if (!defaultPlannedOnly()) {
              <th style="width: 1%; white-space: nowrap" [pSortableColumn]="'planned'" class="text-center">
                <div class="flex items-center justify-center">
                  {{ 'common.labels.planned' | transloco }}
                  @if (dt.sortField === 'planned') {
                    <ces-icon aria-hidden="true">{{ dt.sortOrder === -1 ? 'south' : 'north' }}</ces-icon>
                  } @else {
                    <ces-icon aria-hidden="true" rotate="90">sync_alt</ces-icon>
                  }
                </div>
              </th>
            }
          </tr>
          <!-- Filter Row -->
          <tr>
            @if (!readOnly()) {
              <th style="width: 1%; white-space: nowrap">
                <div class="flex justify-center items-center h-full">
                  <p-checkbox
                    #selectAllCheckbox
                    [binary]="true"
                    [ngModel]="allSelectableSelected()"
                    [indeterminate]="someSelected() && !allSelectableSelected()"
                    (onChange)="onSelectAllChange($event)"
                    [disabled]="selectableRows().length === 0"
                    [ariaLabel]="'common.accessibility.selectAll' | transloco" />
                </div>
              </th>
            }
            <th>
              <p-columnFilter field="name" matchMode="contains" [showMenu]="false" [showClearButton]="false">
                <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                  <div class="p-input-icon-right w-full">
                    <p-iconfield>
                      <input
                        autocomplete="off"
                        spellcheck="false"
                        autocorrect="off"
                        autocapitalize="off"
                        data-gramm="false"
                        type="text"
                        pInputText
                        [ngModel]="value"
                        (input)="onFilterInput($event, filter)"
                        class="p-column-filter w-full"
                        [attr.aria-label]="'common.accessibility.filterName' | transloco" />
                      @if (value) {
                        <p-inputicon styleClass="cursor-pointer" (click)="filter(''); $event.stopPropagation()">
                          <ces-icon size="xl" containerSize="xs">close</ces-icon>
                        </p-inputicon>
                      }
                    </p-iconfield>
                  </div>
                </ng-template>
              </p-columnFilter>
            </th>
            @if (planningMode() === 'measures') {
              <th>
                <p-columnFilter field="typeName" matchMode="in" [showMenu]="false" [showClearButton]="false">
                  <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                    <p-multiselect
                      [ngModel]="value"
                      optionLabel="label"
                      optionValue="value"
                      [options]="typeOptions()"
                      [showClear]="true"
                      [filter]="typeOptions().length >= 10"
                      (onChange)="filter($event.value?.length ? $event.value : null)"
                      (onClear)="filter(null)"
                      appendTo="body"
                      [maxSelectedLabels]="1"
                      [ariaLabel]="'common.dialogs.selectMeasures.filters.byType' | transloco"
                      styleClass="w-full" />
                  </ng-template>
                </p-columnFilter>
              </th>
            }
            <th>
              <p-columnFilter type="boolean" field="obligatoire" [showMenu]="false" [showClearButton]="false" matchMode="equals">
                <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                  <div class="flex items-center justify-center">
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="value"
                      (onChange)="$event.checked ? filter(true) : filter('')"
                      [ariaLabel]="'common.accessibility.filterMandatory' | transloco" />
                  </div>
                </ng-template>
              </p-columnFilter>
            </th>
            <th>
              <p-columnFilter field="locationIds" matchMode="arrayContainsAny" [showMenu]="false" [showClearButton]="false">
                <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                  <p-multiselect
                    [ngModel]="value"
                    optionLabel="label"
                    optionValue="value"
                    [options]="locationOptions()"
                    [showClear]="true"
                    [filter]="locationOptions().length >= 10"
                    (onChange)="filter($event.value?.length ? $event.value : null)"
                    (onClear)="filter(null)"
                    appendTo="body"
                    [maxSelectedLabels]="1"
                    [ariaLabel]="'common.dialogs.selectMeasures.filters.byLocation' | transloco"
                    styleClass="w-full" />
                </ng-template>
              </p-columnFilter>
            </th>
            @if (!defaultPlannedOnly()) {
              <th>
                <p-columnFilter type="boolean" field="planned" [showMenu]="false" [showClearButton]="false" matchMode="equals">
                  <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                    <div class="flex items-center justify-center">
                      <p-checkbox
                        [binary]="true"
                        [ngModel]="value"
                        (onChange)="$event.checked ? filter(true) : filter('')"
                        [ariaLabel]="'common.accessibility.filterPlanned' | transloco" />
                    </div>
                  </ng-template>
                </p-columnFilter>
              </th>
            }
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr [class.row-selected]="isSelected(row) && isRowSelectable(row)" [class.row-inactive]="!row.hasValidFormula">
            @if (!readOnly()) {
              <td class="px-0">
                <div class="flex flex-row justify-center min-w-fit">
                  <div class="aspect-square h-8 flex items-center justify-center">
                    @if (!isRowSelectable(row)) {
                      <span
                        class="disabled-checkbox-visual"
                        role="img"
                        [attr.aria-label]="'common.accessibility.notSelectable' | transloco"
                        [pTooltip]="
                          row.obligatoire && row.planned
                            ? ('entities.campaign.grid.mandatoryNotUnplannableTooltip' | transloco)
                            : !row.hasValidFormula
                              ? ('entities.campaign.grid.measureInactiveTooltip' | transloco)
                              : undefined
                        "
                        [appendTo]="'body'"
                        tooltipPosition="right"></span>
                    } @else {
                      <div (click)="handleRowCheckboxClick($event, row)" class="cursor-pointer">
                        <p-checkbox [binary]="true" [ngModel]="isSelected(row)" [ariaLabel]="getSelectRowAriaLabel(row.name)" />
                      </div>
                    }
                  </div>
                </div>
              </td>
            }
            <td class="font-medium">
              <div class="flex items-center gap-1">
                @if (!row.hasValidFormula) {
                  <ces-suspended-measure-icon [tooltip]="'entities.campaign.grid.measureInactiveTooltip' | transloco" />
                }
                @if (planningMode() === 'measures' && row.measureId && targetAssetId() && hasMeasureReadAccess()) {
                  <!-- Manual segment is correct: planning-data is backend-filtered to manual measures only. -->
                  <a
                    [href]="'/assets/' + targetAssetId() + '/configuration/measures/manual/' + row.measureId + '/info'"
                    target="_blank"
                    rel="noopener"
                    class="underline text-[var(--p-text-color)] hover:text-[var(--p-primary-color)]">
                    {{ row.name }}
                  </a>
                } @else {
                  {{ row.name }}
                }
              </div>
            </td>
            @if (planningMode() === 'measures') {
              <td>{{ displayCell(row.typeName) }}</td>
            }
            <td class="text-center">
              <span class="sr-only">{{ (row.obligatoire ? 'common.labels.yes' : 'common.labels.no') | transloco }}</span>
              @if (row.obligatoire) {
                <ces-icon aria-hidden="true" [size]="'3xl'" [color]="'success'" rounded="true">check_circle</ces-icon>
              } @else {
                <ces-icon aria-hidden="true" [size]="'3xl'" [color]="'disable'" rounded="true">cancel</ces-icon>
              }
            </td>
            <td>{{ displayCell(row.localisation) }}</td>
            @if (!defaultPlannedOnly()) {
              <td class="text-center">
                <span class="sr-only">{{ (row.planned ? 'common.labels.yes' : 'common.labels.no') | transloco }}</span>
                @if (row.planned) {
                  <ces-icon aria-hidden="true" [size]="'3xl'" [color]="'success'" rounded="true">check_circle</ces-icon>
                } @else {
                  <ces-icon aria-hidden="true" [size]="'3xl'" [color]="'disable'" rounded="true">cancel</ces-icon>
                }
              </td>
            }
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="emptyColspan()" class="text-center py-8 text-(color:--text-secondary)">
              @if (defaultPlannedOnly()) {
                {{
                  planningMode() === 'measures'
                    ? ('common.planning.noMeasuresPlanned' | transloco)
                    : ('common.planning.noAuscultationsPlanned' | transloco)
                }}
              } @else {
                {{
                  planningMode() === 'measures'
                    ? ('common.planning.noMeasuresAvailable' | transloco)
                    : ('common.planning.noAuscultationsAvailable' | transloco)
                }}
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    }
  </div>

  @if (isDialog) {
    <div class="flex justify-between items-center pt-(--space-2) border-t border-[var(--border)]">
      <div class="text-sm text-(color:--text-secondary)">
        @if (!isLoading()) {
          {{ 'common.planning.selectedCount' | transloco: { count: selectedCount(), total: selectableRows().length } }}
        }
      </div>
      <div class="flex gap-(--gap-normal)">
        <ces-button-tertiary [label]="'common.buttons.cancel' | transloco" (onClick)="cancel()" />
        @if (!isLoading() && !readOnly()) {
          <ces-button-secondary
            [label]="'entities.campaign.actions.unplan' | transloco"
            icon="event_busy"
            [disabled]="isSaving() || !canUnplan()"
            (onClick)="onUnplan()" />
          <p-button
            [label]="'common.buttons.plan' | transloco"
            icon="pi pi-calendar-plus"
            [disabled]="isSaving() || !canPlan()"
            (onClick)="onPlan()" />
        }
      </div>
    </div>
  }
</div>
